import {
  AsYouType,
  type CountryCode,
  Metadata,
  type MetadataJson
} from 'libphonenumber-js/max';

const DetectionType = {
  Exact: 'EXACT',
  Provisional: 'PROVISIONAL',
  LackInfo: 'LACK_INFO'
} as const;

type DetectionTypeValue = (typeof DetectionType)[keyof typeof DetectionType];

interface DetectionInfo {
  length: number;
  detection: DetectionTypeValue;
}

type FullMetadata = Metadata & {
  metadata: MetadataJson;
};

type Listener = () => void;
const ONLY_DIGITS_REGEXP = /^\d+$/;
const extractDigits = (str: string) => str.replace(/\D/g, '');

// Может устареть в связи добавлением новых телефонных кодов в Canadian Numbering Plan (обычно только добавляются)
const CANADA_LEADING_DIGITS =
  '204|226|236|249|250|257|263|289|306|343|354|365|368|382|403|418|428|431|437|438|450|468|474|506|514|519|568|579|581|584|587|600|604|613|622|633|639|647|672|683|705|709|742|753|778|780|782|807|825|867|879|902|942|873|367|642';

/**
 * PhoneCountryDetector — детектор страны по вводимому номеру телефона.
 *
 * Алгоритм:
 * - Получает calling code через `AsYouType`.
 * - Если код однозначно соответствует одной стране — Exact.
 * - Если несколько стран — проверяются ведущие цифры (подходят только для позитивной фильтрации):
 *     - Полное совпадение → Exact.
 *     - Частичное совпадение → Provisional.
 *     - Совпадений нет → LackInfo.
 *     - Exact совпадение спадает, если ввод уменьшился или изменился в начале
 * События:
 * - Метод `subscribe(fn)` уведомляет при изменении `countryCode`.
 * - Метод `reset()` сбрасывает внутреннее состояние и текущую страну.
 */
export class PhoneCountryDetector {
  private metadata: FullMetadata;
  private asYouType: AsYouType;
  private previousValue = '';
  private previousResult: DetectionInfo | null = null;
  private countryCode: CountryCode | null = null;
  countryCallingCodes;
  private listeners = new Set<Listener>();

  constructor(value = '') {
    this.metadata = new Metadata() as FullMetadata;
    this.asYouType = new AsYouType();
    this.countryCallingCodes = this.metadata.metadata.country_calling_codes;

    if (value) {
      this.asYouType.input(value);
      this.previousValue = value;
    }
  }

  private hasChangeAtStart(value: string) {
    const prevDigits = extractDigits(this.previousValue);
    const valueDigits = extractDigits(value);

    const minLength = Math.min(
      prevDigits.length,
      valueDigits.length,
      this.previousResult?.length ?? Infinity
    );

    for (let idx = 0; idx < minLength; idx++) {
      if (prevDigits[idx] !== valueDigits[idx]) {
        return idx < (this.previousResult?.length ?? 0);
      }
    }

    return false;
  }

  input(value: string) {
    this.asYouType.reset();
    this.asYouType.input(value);

    const hasChangeAtStart = this.hasChangeAtStart(value);
    const hasInputDecreased = this.previousValue.length > value.length;
    this.previousValue = value;

    const shouldDetectCountry =
      !this.previousResult ||
      this.previousResult.detection === DetectionType.LackInfo ||
      this.previousResult.detection === DetectionType.Provisional ||
      (this.previousResult.detection === DetectionType.Exact &&
        (hasInputDecreased || hasChangeAtStart));

    if (shouldDetectCountry) {
      this.detectCountry(value);
    }
  }

  private detectCountry(val: string): void {
    const lackInfoResult = {
      countryCode: null,
      length: val.length,
      detection: DetectionType.LackInfo
    };
    const callingCode = this.asYouType.getCallingCode();
    const possibleCountries = callingCode
      ? this.countryCallingCodes[callingCode]
      : undefined;

    if (val === '+' || !callingCode || !possibleCountries) {
      this.previousResult = lackInfoResult;
      return;
    }

    if (possibleCountries.length === 1) {
      this.previousResult = {
        detection: DetectionType.Exact,
        length: val.length
      };
      this.setCountryCode(possibleCountries[0]);
      return;
    }

    const result = this.detectByLeadingDigits(possibleCountries, val);
    if (result) {
      this.previousResult = result;
      return;
    }

    this.previousResult = {
      detection: DetectionType.Provisional,
      length: val.length
    };
    this.setCountryCode(possibleCountries[0]);
  }

  private detectByLeadingDigits(
    possibleCountries: CountryCode[],
    value: string
  ) {
    let provisionalResult: DetectionInfo | null = null;
    const nationalNumber = this.asYouType.getNumber()?.nationalNumber;

    if (!nationalNumber) {
      return;
    }

    for (let country of possibleCountries) {
      const leadingDigits = this.getLeadingDigits(country);

      if (!leadingDigits) {
        continue;
      }

      if (ONLY_DIGITS_REGEXP.test(leadingDigits)) {
        if (nationalNumber === leadingDigits) {
          this.setCountryCode(country);
          return {
            length: value.length,
            detection: DetectionType.Exact
          };
        }

        const isPrefix =
          leadingDigits.startsWith(nationalNumber) ||
          nationalNumber.startsWith(leadingDigits);
        if (isPrefix) {
          this.setCountryCode(country);
          provisionalResult = {
            length: value.length,
            detection: DetectionType.Provisional
          };
        }

        continue;
      }

      try {
        const regexp = new RegExp('^(?:' + leadingDigits + ')');
        const matches: string[] | null = nationalNumber.match(regexp);

        if (!matches) {
          continue;
        }

        if (matches[0] === nationalNumber) {
          this.setCountryCode(country);
          return {
            length: value.length,
            detection: DetectionType.Exact
          };
        }

        this.setCountryCode(country);
        provisionalResult = {
          length: value.length,
          detection: DetectionType.Provisional
        };
      } catch {
        continue;
      }
    }

    return provisionalResult;
  }

  private getLeadingDigits(country: CountryCode) {
    if (country === 'CA') {
      return CANADA_LEADING_DIGITS;
    }

    this.metadata.selectNumberingPlan(country);
    const leadingDigits: string | number | undefined =
      this.metadata.numberingPlan?.leadingDigits();

    if (typeof leadingDigits === 'number' && leadingDigits === 0) {
      return null;
    }

    return String(leadingDigits);
  }

  getCountryCode() {
    return this.countryCode;
  }

  private setCountryCode(countryCode: CountryCode) {
    this.countryCode = countryCode;
    this.emit();
  }

  reset() {
    this.previousValue = '';
    this.previousResult = null;
    this.asYouType.reset();
    this.countryCode = null;
  }

  subscribe(fn: Listener): () => void {
    this.listeners.add(fn);

    return () => {
      this.listeners.delete(fn);
    };
  }

  private emit() {
    const listeners = Array.from(this.listeners);
    for (const listener of listeners) {
      try {
        listener();
      } catch (err) {
        console.error('PhoneDetector listener error', err);
      }
    }
  }
}
